import { BACKEND_URL, LICENSE_API_KEY } from "../env";
import {
  normalizeDashboardInsightsPayload,
  normalizeDetailedReportPayload,
  normalizeInsightsPayload,
  normalizeProfilePayload,
  normalizeSessionReportPayload,
} from "./evalCategoryUtils";
import apiClient, { buildUrl, getToken } from "./axios";
import { UserAttributes } from "./types";
import { getTenantKey } from "./utils";

const mapResponseData = (mapper: (data: any) => any) => (response: any) => {
  if (response?.data != null) {
    response.data = mapper(response.data);
  }
  return response;
};

export const api = {
  login: (data: any) => {
    return apiClient.post("/auth/login", data);
  },
  createCharacter: (data: any) => {
    return apiClient.post("/characters", data);
  },
  getMe: () => {
    return apiClient.get("/user-management/users/me");
  },
  getCharacters: () => {
    return apiClient.get("/characters");
  },
  startCall: (data: any) => {
    return apiClient.post("/sessions/sessions", data);
  },
  endCall: (callId: string, data: any) => {
    return apiClient.post(`/sessions/${callId}/end`, data);
  },
  watchReport: (callId: string) => {
    return apiClient.get(`/post-roleplay/${callId}/reports/watch`);
  },
  watchDetailedReport: (callId: string) => {
    return apiClient.get(`/post-roleplay/${callId}/detailedReports/watch`);
  },
  fetchReport: (sessionId: string, data: any) => {
    return apiClient
      .get(`/post-roleplay/${sessionId}/reports`)
      .then(mapResponseData(normalizeSessionReportPayload));
  },
  getReports: (callId: string) => {
    return apiClient
      .get(`/post-roleplay/${callId}/reports`)
      .then(mapResponseData(normalizeSessionReportPayload));
  },
  postDetailedReport: (sessionId: string, data: { messages: any[] }) => {
    return apiClient.post(`/post-roleplay/${sessionId}/detailedReports`, data);
  },
  getDetailedReport: (sessionId: string) => {
    return apiClient
      .get(`/post-roleplay/${sessionId}/detailedReports`)
      .then(mapResponseData(normalizeDetailedReportPayload));
  },
  subscribeToReport: (sessionId: string) => {
    return apiClient.post(`/post-roleplay/${sessionId}/reports/subscribe`);
  },
  sendFullTextResponse: (callId: string, data: any) => {
    return apiClient.post(`/call/${callId}/query-response`, data);
  },
  uploadCallPart: (callId: string, form: any) => {
    return fetch(`${buildUrl(`call/${callId}/response`)}`, {
      method: "POST",
      headers: {
        Authorization: `${getToken()}`,
      },
      body: form,
    });
  },
  socialLogin: (data: any) => {
    return apiClient.post("/auth/login/social", data);
  },
  initEmailVerification: (id: any) => {
    return apiClient.post(`/users/${id}/email/verify/initiate`);
  },
  verifyEmail: (id: any, data: any) => {
    return apiClient.post(`/users/${id}/email/verify`, data);
  },
  signup: (data: any) => {
    return apiClient.post("/auth/register", data).then((res) => res.data);
  },
  logout: () => {
    return apiClient.post("/auth/logout").then((res) => res.data);
  },
  changePassword: (
    id: string,
    data: {
      oldPassword: string;
      newPassword: string;
    }
  ) => {
    return apiClient.patch(`/users/${id}/password`, data);
  },
  forgotPassword: (data: any) => {
    return apiClient.post("/auth/forgot-password", data);
  },
  resetPassword: (data: any) => {
    return apiClient.post("/auth/reset-password", data);
  },
  getUserProfile: (id: string) => {
    return apiClient.get(`/user-management/users/${id}`).then((res) => res.data);
  },
  saveLastActive: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  saveUserProfile: (id: string, data: any) => {
    return apiClient.put(`/users/${id}`, data);
  },
  getProgressDashboard: (userId: string, month: string, year: string) => {
    return apiClient
      .get(`/users/${userId}/progress-dashboard?month=${month}&year=${year}`)
      .then((r) => r.data);
  },
  getDashboard: (userId: string) => {
    return apiClient.get(`/users/${userId}/dashboard`).then((r) => r.data);
  },
  intro: (userId: string) => {
    return fetch(`${buildUrl(`users/${userId}/intro`)}`, {
      method: "POST",
      headers: {
        Authorization: `${getToken()}`,
      },
    });
  },
  getPredesignedUrl: ({
    id,
    mimeType,
    uploadType,
  }: {
    id: string;
    mimeType: string;
    uploadType: string;
  }) => {
    let url = `/users/${id}/images/signed-url?mimeType=${mimeType}&uploadType=${uploadType}`;

    return apiClient.get(url).then((r) => r.data);
  },
  getAssemblyAIToken: () => {
    let url = "/auth/assembly-ai/token";
    return apiClient.post(url);
  },
  chatStream: (data: { query: string; callId: string }) => {
    const url = `call/${data.callId}/query`;
    return fetch(`${buildUrl(url)}`, {
      method: "POST",
      headers: {
        Authorization: `${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: data.query }),
    }).then((res) => res.text());
  },
  createPlayback: ({
    sessionId,
    userId,
  }: {
    sessionId: string;
    userId: string;
  }) => {
    let url = `call/create-playback/${userId}/${sessionId}`;
    return apiClient.post(
      url,
      {},
      {
        headers: {
          Authorization: `${getToken()}`,
        },
      }
    );
  },
  createCertification: (data: any) => {
    let url = `/certifications/certifications/metadata`;
    return apiClient.post(url, data);
  },
  getCertifications: ({
    isActive,
    status,
    name,
    after
  }: {
    isActive?: boolean;
    status?: string;
    name?: string;
    after?: string;
  }) => {
    let url = `/certifications/certifications`;
    let params: { isActive?: boolean, status?: string, name?: string, after?: string } = {};
    if (isActive !== undefined) {
      params.isActive = isActive;
    }
    if (status && status !== "all") {
      params.status = status;
    }
    if (name) {
      params.name = name;
    }
    if (after) {
      params.after = after;
    }
    return apiClient.get(url, { params });
  },
  searchCertificationsUsers: ({search, group}: {search: string, group?: string}) => {
    let url = `/user-management/users/certifiable`;
    let params: { search?: string, group?: string } = {};
    if (search) {
      params.search = search;
    }
    if (group) {
      params.group = group;
    }
    return apiClient.get(url, { params });
  },
  getTerritories: () => {
    let url = `/user-management/territories`;
    return apiClient.get(url);
  },
  getZones: () => {
    let url = `/user-management/zones`;
    return apiClient.get(url);
  },
  createZone: (data: any) => {
    let url = `/user-management/zones`;
    return apiClient.post(url, data);
  },
  updateZone: (id: string, data: any) => {
    let url = `/user-management/zones/${id}`;
    return apiClient.patch(url, data);
  },
  deleteZone: (id: string) => {
    let url = `/user-management/zones/${id}`;
    return apiClient.delete(url);
  },
  createTerritory: (data: any) => {
    let url = `/user-management/territories`;
    return apiClient.post(url, data);
  },
  updateTerritory: (id: string, data: any) => {
    let url = `/user-management/territories/${id}`;
    return apiClient.patch(url, data);
  },
  deleteTerritory: (id: string) => {
    let url = `/user-management/territories/${id}`;
    return apiClient.delete(url);
  },
  getRegions: () => {
    let url = `/user-management/regions`;
    return apiClient.get(url);
  },
  createRegion: (data: any) => {
    let url = `/user-management/regions`;
    return apiClient.post(url, data);
  },
  updateRegion: (id: string, data: any) => {
    let url = `/user-management/regions/${id}`;
    return apiClient.patch(url, data);
  },
  deleteRegion: (id: string) => {
    let url = `/user-management/regions/${id}`;
    return apiClient.delete(url);
  },
  getProducts: () => {
    let url = `/certifications/products`;
    return apiClient.get(url);
  },
  getAssignments: ({ after }: { after?: string } = {}) => {
    const url = `/certifications/assignments`;
    const params: { after?: string } = {};
    if (after) {
      params.after = after;
    }
    return apiClient.get(url, { params });
  },
  getCertificationList: () => {
    let url = `/certifications`;
    return apiClient.get(url);
  },
  getCertificationAssets: () => {
    let url = `/assets/certifications`;
    return apiClient.get(url);
  },
  searchUsers: () => {
    let url = `/user-management/users/search/filtered`;
    return apiClient.get(url).then((res) => res.data);
  },
  getUsers: () => {
    let url = `/user-management/users`;
    return apiClient.get(url).then((res) => res.data);
  },
  updateUserAttributes: (id: string, data: UserAttributes) => {
    let url = `/user-management/users/${id}/attributes`;
    return apiClient.post(url, data);
  },
  getUserAttributes: (id: string) => {
    let url = `/users/${id}/attributes`;
    return apiClient.get(url);
  },
  searchCertifications: (data: any) => {
    let url = `/certifications/assignments`;
    return apiClient.get(url, { params: data });
  },
  fetchPersonalityContext: (data: any) => {
    let url = `/agents/personality-context`;
    return apiClient.post(url, data);
  },
  createUser: (data: any) => {
    let url = `/user-management/users/create`;
    return apiClient.post(url, data);
  },
  getAllReports: (after?: string, filter?: any) => {
    let url = `/sessions/sessions`;
    const params: any = after ? { after } : {};
    if (filter?.sortBy) {
      params.sortBy = filter.sortBy;
    }
    if (filter?.practitionerType && filter.practitionerType !== "all") {
      params.practitionerType = filter.practitionerType;
    }
    if (filter?.month) {
      params.month = filter.month;
    }
    if (filter?.year) {
      params.year = filter.year;
    }
    return apiClient.get(url, { params });
  },
  getSessionHistoryMetrics: ({month, year}: {month: string, year: string}) => {
    let url = `/metrics/monthly-summary`;
    let params = {
      month,
      year,
    }
    return apiClient.get(url, { params });
  },
  getVideoPlayback: (userId: string, sessionId: string) => {
    let url = `/call/get-playback/${userId}/${sessionId}`;
    return apiClient.get(url);
  },
  getTeamReports: (managerId: string) => {
    let url = `/call/get-team-reports/${managerId}`;
    return apiClient.get(url);
  },
  loginCallback: (code: string) => {
    let url = `sso/callback`;
    return fetch(`${buildUrl(url)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ code }),
    });
  },
  attemptCertification: (data: any) => {
    let url = `/certifications/attempts`;
    return apiClient.post(url, data);
  },
  /**
   * 
   * @param {
      "certificationAttemptId": "some-uuid-here",
      "review": {
            certification_review: [{
            compliance_task: "Adherence to HIPAA Guidelines",
            grade: "Pass",
            }]
        }
      } 
   * @returns 
   */
  reviewCertification: (data: any) => {
    let url = `/call/certification-review`;
    return apiClient.post(url, data);
  },
  getCertificationAttempts: (assignmentId: string) => {
    let url = `/certifications/assignments/${assignmentId}/attempts`;
    return apiClient.get(url);
  },
  fetchBrandAndPIContext: (args: {
    brandAndPI: string;
    sentence: string;
  }) => {
    let url = `/call/brand_and_pi_context`;
    return apiClient.post(url, args);
  },
  getSessionDetails: (data: {
    userId: string;
    sessionId: string;
  }) => {
    let url = `/call/session-details`;
    return apiClient.post(url, data);
  },
  getLicenses: () => {
    const domain = getTenantKey();
    const firstPart = domain.split(".praxispro.ai")[0];
    // if it has -dev then remove it
    const tenant = firstPart.replace("-dev", "");
    const url = `${BACKEND_URL}/licenses/${tenant}`
    return apiClient.get(url, {
      headers: {
        "x-api-key": LICENSE_API_KEY,
      },
    });
  },
  getPreLogin: () => {
    const domain = getTenantKey();
    const firstPart = domain.split(".praxispro.ai")[0];
    // if it has -dev then remove it
    const tenant = firstPart.replace("-dev", "");
    const url = `${BACKEND_URL}api/tenant/${tenant}/assets/pre-login`
    return apiClient.get(url, {
      headers: {
        "x-api-key": LICENSE_API_KEY ,
      },
    });
  },
  getPreLoginLogos: () => {
    const domain = getTenantKey();
    const firstPart = domain.split(".praxispro.ai")[0];
    // if it has -dev then remove it
    const tenant = firstPart.replace("-dev", "");
    const url = `${BACKEND_URL}api/tenant/${tenant}/assets/pre-login-logos`
    return apiClient.get(url, {
      headers: {
        "x-api-key": LICENSE_API_KEY,
      },
    });
  },
  getCredentials: () => {
    const url = `assets/credentials`
    return apiClient.get(url);
  },
  getProfile: () => {
    const url = `assets/profile`
    return apiClient.get(url).then(mapResponseData(normalizeProfilePayload));
  },
  getDashboardInsights: () => {
    const url = `assets/dashboard_insights`
    return apiClient.get(url).then(mapResponseData(normalizeDashboardInsightsPayload));
  },
  getLoadingTips: () => {
    return apiClient.get("assets/loading_tips");
  },
  // dashboard apis
  getTeamMetrics: ({level, userID}: {level?: string, userID?: string}) => {
    let url = "metrics/team";
    let params = "";
    if (level) {
      params += `level=${level}`;
    }
    if (userID) {
      params += `${params ? "&" : ""}userID=${userID}`;
    }
    if (params) {
      url += `?${params}`;
    }
    return apiClient.get(url).then(mapResponseData(normalizeInsightsPayload));
  },
  getTeamLeaderBoard: ({level, userID, dateRange, rankBy}: {level?: string, userID?: string, dateRange?: string, rankBy?: string}) => {
    let url = "metrics/team-leaderboard";
    let params = "";
    if (level) {
      params += `level=${level}`;
    }
    if (userID) {
      params += `${params ? "&" : ""}userID=${userID}`;
    }
    if (dateRange) {
      params += `${params ? "&" : ""}days=${dateRange}`;
    }
    if (rankBy) {
      params += `${params ? "&" : ""}rankBy=${rankBy}`;
    }
    if (params) {
      url += `?${params}`;
    }
    return apiClient.get(url);
  },
  getPraxisScores: () => {
    const url = "metrics/praxis-scores";
    return apiClient.get(url);
  },
  getInsights: () => {
    const url = "metrics/insights";
    return apiClient.get(url).then(mapResponseData(normalizeInsightsPayload));
  },
  getLocationRankings: () => {
    const url = "metrics/location-rankings";
    return apiClient.get(url);
  },
  sessionCounts: () => {
    const url = "metrics/session-counts";
    return apiClient.get(url);
  },
  sessionDuration: () => {
    const url = "metrics/sessions-duration";
    return apiClient.get(url);
  },
  sessionHistory: () => {
    // metrics/sessions-history?days=5
    const url = "metrics/sessions-history?days=5"
    return apiClient.get(url);
  },

  // cohort apis
  assignGroup: (data: { group: string }) => {
    const url = "/user-management/users/groups/assign";
    return apiClient.post(url, data);
  },
  getOnboardingGroups: () => {
    const url = "assets/onboarding/groups";
    return apiClient.get(url);
  },

  // onboarding apis
  completeQuestionnaire: () => {
    const url = "/user-management/users/complete-questionnaire";
    return apiClient.post(url);
  },
  getRoadMap: () => {
    const url = "/user-management/users/roadmaps";
    return apiClient.get(url);
  },

  // notification apis
  getNotifications: (cursor?: string, category?: string) => {
    const url = "/notifications";
    const categoryFilter = category && category !== "all" ? { "filter.category": `$or:${category}` } : {};
    return apiClient.get(url, { params: { ...categoryFilter, cursor } });
  },
  getNotificationPreferences: () => {
    const url = "/notifications/preferences";
    return apiClient.get(url);
  },
  updateNotificationPreferences: (data: any) => {
    const url = "/notifications/preferences";
    return apiClient.patch(url, data);
  },
  getPendingNotifications: () => {
    const url = "/notifications/has-pending";
    return apiClient.get(url);
  },
  seeNotifications: () => {
    const url = "/notifications/see";
    return apiClient.post(url);
  },
  errorFlag: (sessionId: string, data: any) => {
    const url = `/call/${sessionId}/error-flag`;
    return apiClient.patch(url, data);
  },
  registerDevice: (data: {
    deviceID: string;
    firebaseToken: string;
  }) => {
    const url = "/notifications/devices";
    return apiClient.post(url, data);
  },
  getEngagementReport: (data: any) => {
    // /metrics/engagement?sortBy=complete&sortDirection=DESC&hasComplete=true
    let url = "/metrics/engagement";
    let paramText: string[] = [];
    if (data?.sortBy) {
      paramText.push(`sortBy=${data.sortBy}`);
      if (data?.sortDirection) {
        paramText.push(`sortDirection=${data.sortDirection}`);
      }
    }
    if (data?.hasComplete) {
      paramText.push(`hasComplete=${data.hasComplete ? "true" : "false"}`);
    }
    if (data?.hasIncomplete) {
      paramText.push(`hasIncomplete=${data.hasIncomplete ? "true" : "false"}`);
    }
    if (data?.days) {
      paramText.push(`days=${data.days}`);
    }
    if (data?.quarter) {
      paramText.push(`quarter=${data.quarter}`);
    }
    if (data?.startDate && data?.endDate) {
      paramText.push(`dateBtw=${data.startDate},${data.endDate}`);
    }
    if (data?.cohort) {
      paramText.push(`cohort=${data.cohort}`);
    }
    const fullUrl = `${url}?${paramText.join("&")}`;  
    return apiClient.get(fullUrl);
  },
  // file upload apis
  uploadSalesAidFiles: (data: any) => {
    const url = "/sales-aid/files";
    return apiClient.post(url, data);
  },
  getFaqAssets: (params?: { search?: string; category?: string }) => {
    return apiClient.get("/assets/faq", { params });
  },
  getSalesAidFiles: (sort?: { sort_by: string; sort_dir: string }) => {
    const url = "/sales-aid/files";
    return apiClient.get(url, { params: sort });
  },
  getSalesAidManagementFiles: (sort?: { sort_by: string; sort_dir: string }) => {
    const url = "/sales-aid/management/files";
    return apiClient.get(url, { params: sort });
  },
  getSalesAidCertificationFiles: () => {
    const url = "/sales-aid/certifications/files";
    return apiClient.get(url);
  },
  getCertificationSalesAidFiles: (certificationId: string) => {
    const url = `/sales-aid/files/${certificationId}`;
    return apiClient.get(url);
  },
  deleteSalesAidFile: (id: string) => {
    const url = `/sales-aid/files/${id}`;
    return apiClient.delete(url);
  },
  updateSalesAidFile: (
    id: string | number,
    data: { name?: string; code?: string; groups?: string[] }
  ) => {
    const url = `/sales-aid/files/${id}`;
    return apiClient.patch(url, data);
  },
  getSalesAidFileSignedURL: (fileId: string, sessionId?: string) => {
    const url = sessionId
      ? `/sales-aid/files/${fileId}/signed-url?sessionID=${encodeURIComponent(sessionId)}`
      : `/sales-aid/files/${fileId}/signed-url`
    return apiClient.get(url);
  },
  getCertificationAssignments: (certificationID: string, data: {
    search?: string;
    status?: string;
    aiRecommendation?: string;
    after?: string;
  }) => {
    let params: Record<string, string | undefined> = {
      search: data.search,
      status: data.status && data.status === "all" ? undefined : data.status,
      aiRecommendation: data.aiRecommendation && data.aiRecommendation === "all" ? undefined : data.aiRecommendation,
      after: data.after,
    }
    const url = `/certifications/reviews?certificationID=${certificationID}`;
    return apiClient.get(url, { params });
  },
  getAttemptReview: (certificationAssignmentId: string, attempt: string | number) => {
    const url = `/certifications/reviews/${certificationAssignmentId}/${attempt}`;
    return apiClient.get(url);
  },
  watchAttemptReview: (certificationAssignmentId: string, attempt: string | number) => {
    const url = `/certifications/reviews/${certificationAssignmentId}/${attempt}/watch`;
    return apiClient.get(url, { timeout: 30_000 });
  },
  saveAttemptReview: (certificationAttemptId: string, data: any) => {
    const url = `/certifications/reviews/${certificationAttemptId}`;
    return apiClient.post(url, data);
  },
  deleteCertification: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}`;
    return apiClient.delete(url);
  },
  getCertificationDetails: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}`;
    return apiClient.get(url);
  },
  getCertificationCallFlowDetails: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}/call-flow`;
    return apiClient.get(url);
  },
  getCertificationCategories: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}/categories`;
    return apiClient.get(url);
  },
  getCertificationCategoriesByDocument: (salesAidFileId: string | number) => {
    const url = `/certifications/categories?salesAidFileId=${salesAidFileId}`;
    return apiClient.get(url);
  },
  saveManualReview: (certificationAssignmentId: string, data: any) => {
    const url = `/certifications/assignments/${certificationAssignmentId}/manual-review`;
    return apiClient.post(url, data);
  },
  getAssignmentDetails: (certificationAssignmentId: string, _attempt: string | number) => {
    const url = `/certifications/assignments/${certificationAssignmentId}/details`;
    return apiClient.get(url);
  },
  attachImageToCertification: (certificationId: string, data: {index: number, imageURL: string, imageName?: string}) => {
    const url = `/certifications/certifications/${certificationId}/image`;
    return apiClient.put(url, data);
  },
  saveCallFlow:(certificationId: string, data: {callFlow: {question: string, category: string}[]}) => {
    const url = `/certifications/certifications/${certificationId}/call-flow`;
    return apiClient.patch(url, data);
  },
  saveCallFlowPost:(certificationId: string, data: {callFlow: {question: string, category: string}[]}) => {
    const url = `/certifications/certifications/${certificationId}/call-flow`;
    return apiClient.post(url, data);
  },
  updateAdminReview: (assignmentId: string, data: any) => {
    const url = `/certifications/assignments/${assignmentId}/admin-override`;
    return apiClient.post(url, data);
  },
  exportCertification: (data: any) => {
    const url = `/certifications/exports`;
    return apiClient.post(url, data);
  },
  startCertification: (assignmentId: string) => {
    const url = `/simulations/start`;
    return apiClient.post(url, { assignmentId });
  },
  nextCertificationQuestion: (assignmentId: string, attemptNumber: number, step: number, audioUrl: string) => {
    const url = `/simulations/next/${assignmentId}/${attemptNumber}`;
    return apiClient.post(url, { step, audioUrl });
  },
  endCertification: (assignmentId: string, attemptNumber: number, step: number, audioUrl: string) => {
    const url = `/simulations/end/${assignmentId}/${attemptNumber}`;
    return apiClient.post(url, { step, audioUrl });
  },
  deleteAttachment: (certificationId: string, data: {index: number}) => {
    const url = `/certifications/certifications/${certificationId}/image`;
    return apiClient.delete(url, { data });
  },
  updateHelpTooltipTutorial: (data: { currentStep: number; completed: boolean }) => {
    const url = "/user-management/users/update-help-tooltip-tutorial";
    return apiClient.post(url, data);
  },
  updateCertification: (certificationId: string, data: any) => {
    const url = `/certifications/certifications/${certificationId}/metadata`;
    return apiClient.patch(url, data);
  },
  editCertification: (certificationId: string, data: any) => {
    const url = `/certifications/certifications/${certificationId}`;
    return apiClient.put(url, data);
  },
  patchCertificationAssignments: (certificationId: string, data: any) => {
    const url = `/certifications/certifications/${certificationId}/assignments`;
    return apiClient.patch(url, data);
  },
  putCertificationAssignments: (
    certificationId: string,
    data: { users: string[] }
  ) => {
    const url = `/certifications/certifications/${certificationId}/assignments`;
    return apiClient.put(url, data);
  },
  duplicateCertification: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}/duplicate`;
    return apiClient.post(url);
  },
  // survey apis
  validateSurveyStatus: (sessionId: string) => {
    const url = `/surveys/feedback/${sessionId}`;
    return apiClient.get(url);
  },
  registerSurveyCompletion: (data: { sessionID: string; surveyID: string }) => {
    const url = `/surveys/feedback`;
    return apiClient.post(url, data);
  },
  submitUserReport: (data: {
    title: string;
    description: string;
    type: string;
    browserInfo?: string;
    osInfo?: string;
    currentUrl?: string;
  }) => apiClient.post<{ message: string; jiraKey: string }>(`/surveys/user-report`, data),
  getAttachmentUploadUrl: (data: { filename: string; mimeType: string }) =>
    apiClient.post<{ uploadUrl: string; key: string }>(
      `/surveys/user-report/attachments/upload-url`,
      data
    ),
  attachFilesToReport: (jiraKey: string, keys: string[]) =>
    apiClient.post(`/surveys/user-report/${jiraKey}/attachments`, { keys }),

  // certification comments apis
  getCertificationComments: (assignmentId: string) => {
    const url = `/certifications/assignments/${assignmentId}/comments`;
    return apiClient.get(url);
  },
  saveCertificationComment: (assignmentId: string, data: any) => {
    const url = `/certifications/assignments/${assignmentId}/comments`;
    return apiClient.post(url, data);
  },
  getAssignmentSummary: (assignmentId: string) => {
    const url = `/certifications/assignments/${assignmentId}/summary`;
    return apiClient.get(url);
  },
  getLastHCP: () => {
    return apiClient.get("/sessions/last-hcp");
  },
  getAllAssignments: (certificationId: string) => {
    const url = `/certifications/certifications/${certificationId}/assignments`;
    return apiClient.get(url);
  },
  getWeekPractice: () => {
    const url = "/metrics/week-practice";
    return apiClient.get(url);
  },
  getStreak: () => {
    const url = "/metrics/streak";
    return apiClient.get(url);
  },
};
